// contexts/PollingContext.tsx
import React, {createContext, useContext, useState, useCallback, useEffect, useRef} from 'react';
import {JOB_STATUS} from "../types/reconciliation";

interface PollingContextType {
    isPolling: boolean;
    error: string | null;
    responseStatus: string | undefined;
    startPolling: (id: string, callbackRefetch: () => void) => void;
    stopPolling: (callbackRefetch: () => void) => void;
}

const PollingContext = createContext<PollingContextType | undefined>(undefined);

export const PollingProvider = ({children, getStatusFunction}: { children: React.ReactNode, getStatusFunction: (id: string) => Promise<any> }) => {
    const [isPolling, setIsPolling] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [responseStatus, setResponseStatus] = useState<string | undefined>();

    const intervalId = useRef<NodeJS.Timeout | null>(null)

    const stopPolling = useCallback((callbackRefetch: () => void) => {
        if (intervalId.current) {
            clearInterval(intervalId.current);
            intervalId.current = null;
        }
        setIsPolling(false);
        callbackRefetch()
    }, []);

    const startPolling = useCallback(async (id: string, callbackRefetch: ()=> void) => {
        setIsPolling(true);
        setError(null);

        const id_ = setInterval(async () => {
            try {
                const response = await getStatusFunction(id);
                setResponseStatus(response?.status);

                if (response?.status === JOB_STATUS.ERROR) {
                    setError("Errore durante l'elaborazione");
                    stopPolling(callbackRefetch);
                } else if (response?.status === JOB_STATUS.DONE) {
                    stopPolling(callbackRefetch);
                }
            } catch (err) {
                setError("Si è verificato un Errore");
                stopPolling(callbackRefetch);
            }
        }, 5000);

        intervalId.current = id_;
    }, [getStatusFunction, stopPolling]);

    useEffect(() => {
        return () => {
            if (intervalId.current) {
                clearInterval(intervalId.current);
            }
        };
    }, []);

    return (
        <PollingContext.Provider value={{isPolling, error, responseStatus, startPolling, stopPolling}}>
            {children}
        </PollingContext.Provider>
    );
};

export const usePolling = () => {
    const context = useContext(PollingContext);
    if (context === undefined) {
        throw new Error('Errore');
    }
    return context;
};